import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  makeStyles,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Button,
  Divider,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteIcon from "@material-ui/icons/Delete";
import { inputFieldStyles, commonInlineStyles } from "../../styles";

const useStyles = makeStyles((theme) => ({
  formTitle: {
    marginBottom: theme.spacing(4),
    fontWeight: 700,
    fontSize: 32,
    position: "relative",
  },
  formField: {
    width: "100%",
    "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
  },
  button: { minWidth: 180, borderRadius: 30 },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.75rem",
    marginTop: theme.spacing(0.5),
  },
  tableHeader: {
    fontWeight: 600,
    backgroundColor: "#F0F0F0",
  },
  tableCell: {
    verticalAlign: "middle",
  },
  addSiteButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    borderRadius: 999,
    textTransform: "none",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "#000",
    color: "#000",
    padding: theme.spacing(0.5, 1.5),
    minWidth: "auto",
    fontSize: 12,
    lineHeight: 1.5,
  },
}));

const createEmptyPlant = () => ({
  mfgPlantID: "",
  manufacturingPlantIdSameAsASL: "",
  mfgPlantShortName: "",
});

const createEmptySite = () => ({
  siteID: "",
  siteShortName: "",
  siteHasMultiplePlants: "",
  manufacturingPlantDataList: [createEmptyPlant()],
});

/** Normalize incoming data to SiteDetails shape: siteID, manufacturingPlantDataList, mfgPlantID, mfgPlantShortName */
function normalizeToSiteRows(data) {
  if (!data) return [createEmptySite()];

  if (data.cmoSites && Array.isArray(data.cmoSites) && data.cmoSites.length > 0) {
    const normalizedSites = data.cmoSites.map((site) => {
      if (site?.manufacturingPlantDataList) {
        const plants = site?.manufacturingPlantDataList && Array.isArray(site.manufacturingPlantDataList) && site.manufacturingPlantDataList.length > 0
          ? site.manufacturingPlantDataList.map((plant) => ({
            ...plant?.mfgPlantDetails,
            manufacturingPlantIdSameAsASL:
              plant?.mfgPlantDetails?.mfgPlantID && plant?.mfgPlantDetails?.aslNumber && String(plant?.mfgPlantDetails.aslNumber).trim() === String(plant?.mfgPlantDetails.mfgPlantID).trim()
                ? "Yes"
                : "No",
          }))
          : [createEmptyPlant()];

        return {
          ...site?.siteDetails,
          siteHasMultiplePlants: plants.length > 1 ? "Yes" : "No",
          manufacturingPlantDataList: plants,
        };
      } else if (site?.siteManufacturingPlants) {
        const plants = site?.siteManufacturingPlants && Array.isArray(site.siteManufacturingPlants) && site.siteManufacturingPlants.length > 0
          ? site.siteManufacturingPlants.map((plant) => ({
            ...plant,
            // manufacturingPlantIdSameAsASL:
            //   plant?.mfgPlantID && plant?.aslNumber && String(plant?.aslNumber).trim() === String(plant?.mfgPlantID).trim()
            //     ? "Yes"
            //     : "No",
          }))
          : [createEmptyPlant()];

        return {
          ...site,
          siteHasMultiplePlants: plants.length > 1 ? "Yes" : "No",
          manufacturingPlantDataList: plants,
        };
      } else {
        return createEmptySite()
      }

    });
    return normalizedSites;
  } else {
    return [createEmptySite()]
  }

  // // From cmoSites (API/stepper shape)
  // if (data.cmoSites && Array.isArray(data.cmoSites) && data.cmoSites.length > 0) {
  //   return data.cmoSites.map((site) => {
  //     const siteDetails = site?.siteDetails || site || {};
  //     const plants = site?.manufacturingPlantDataList && Array.isArray(site.manufacturingPlantDataList) && site.manufacturingPlantDataList.length > 0
  //       ? site.manufacturingPlantDataList.map((plant) => {
  //         const p = plant?.mfgPlantDetails != null ? plant.mfgPlantDetails : plant;
  //         return {
  //           ...createEmptyPlant(),
  //           mfgPlantID: p?.mfgPlantID ?? p?.manufacturingPlantId ?? "",
  //           manufacturingPlantIdSameAsASL:
  //             p?.mfgPlantID && p?.aslNumber && String(p.aslNumber).trim() === String(p.mfgPlantID).trim()
  //               ? "Yes"
  //               : p?.manufacturingPlantIdSameAsASL ?? "",
  //           mfgPlantShortName: p?.mfgPlantShortName ?? p?.manufacturingPlantShortName ?? "",
  //         };
  //       })
  //       : [createEmptyPlant()];
  //     return {
  //       ...createEmptySite(),
  //       siteID: siteDetails.siteID ?? siteDetails.siteId ?? "",
  //       siteShortName: siteDetails.siteShortName ?? "",
  //       siteHasMultiplePlants: plants.length > 1 ? "Yes" : plants.length === 1 ? "No" : (siteDetails.siteHasMultiplePlants ?? ""),
  //       siteManufacturingPlants: plants,
  //     };
  //   });
  // }

  // // From siteRows (flat shape; support both naming conventions)
  // const rows = data.siteRows || [];
  // if (rows.length === 0) return [createEmptySite()];

  // return rows.map((site) => {
  //   const plants = (site.siteManufacturingPlants ?? site.manufacturingPlants) ?? [];
  //   const plantList = (Array.isArray(plants) ? plants : [plants]).length > 0
  //     ? (Array.isArray(plants) ? plants : [plants]).map((p) => ({
  //       ...createEmptyPlant(),
  //       mfgPlantID: p?.mfgPlantID ?? p?.manufacturingPlantId ?? "",
  //       manufacturingPlantIdSameAsASL: p?.manufacturingPlantIdSameAsASL ?? "",
  //       mfgPlantShortName: p?.mfgPlantShortName ?? p?.manufacturingPlantShortName ?? "",
  //     }))
  //     : [createEmptyPlant()];
  //   return {
  //     ...createEmptySite(),
  //     siteID: site?.siteID ?? site?.siteId ?? "",
  //     siteShortName: site?.siteShortName ?? "",
  //     siteHasMultiplePlants: site?.siteHasMultiplePlants ?? "",
  //     siteManufacturingPlants: plantList,
  //   };
  // });
}


const SiteIDPlantIDDetails = forwardRef(
  (
    {
      initialData = {},
      onChange,
      editMode: initialEditMode = false,
      onEditModeChange,
      vendorMosymphonyID,
      brandOwnerMosymphonyID,
      originalSiteDetails,
      // onSiteCountChange
    },
    ref
  ) => {
    const classes = useStyles();
    const editMode = initialEditMode;
    const cmoShortName = initialData?.cmoShortName || "";
    const resolvedInitial = (() => {
      const base = { ...initialData };
      const normalized = normalizeToSiteRows(base);
      return {
        cmoHasMultipleSites: typeof base.cmoHasMultipleSites === "boolean"
          ? base.cmoHasMultipleSites === true ? "Yes" : "No"
          : (base.cmoHasMultipleSites || ""),
        siteRows: normalized.filter((val) => val?.markForDelete !== true).map((val)=>({...val,manufacturingPlantDataList: val.manufacturingPlantDataList.filter((val)=> val?.markForDelete !== true)})),
      };
    })();

    const [cmoHasMultipleSites, setCmoHasMultipleSites] = useState(
      resolvedInitial.cmoHasMultipleSites || ""
    );
    const [siteRows, setSiteRows] = useState(
      resolvedInitial.siteRows || [createEmptySite()]
    );
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [siteDetailsChanged, setSiteDetailsChanged] = useState(false);
    // const [originalData, setOriginalData] = useState({ ...originalSiteDetails })
    // let originalData = { ...originalSiteDetails }

    useEffect(() => {
      if (onChange && editMode) {
        onChange(
          siteDetailsChanged
        );
      }
    }, [siteDetailsChanged])

    useEffect(() => {
      // Skip if change came from prop
      if (editMode === initialEditMode) return;

      if (onEditModeChange) {
        onEditModeChange(editMode);
      }
    }, [editMode, onEditModeChange, initialEditMode]);

 
    // useEffect(() => {
    //   console.log("jjjj")
    //   const base = { ...initialData };
    //   const normalized = normalizeToSiteRows(base);
    //   const cmoVal = typeof base.cmoHasMultipleSites === "boolean"
    //     ? base.cmoHasMultipleSites === true ? "Yes" : "No"
    //     : (base.cmoHasMultipleSites || "");
    //   setCmoHasMultipleSites(cmoVal);
    //   setSiteRows(normalized.length > 0 ? normalized : [createEmptySite()]);
    //   setErrors({});
    //   setOriginalData({...originalSiteDetails})
    // }, [initialData, originalSiteDetails]);



    // When "CMO has multiple sites" is "No", ensure only one site row
    useEffect(() => {
      if (cmoHasMultipleSites === "No" && siteRows.length > 1) {
        setSiteRows((prev) => [prev[0] || createEmptySite()]);
      }
    }, [cmoHasMultipleSites, siteRows.length]);

    // Auto-populate site short name from CMO short name when CMO has multiple sites = No
    // Only populate if site short name is not already set
    useEffect(() => {
      if (editMode && cmoHasMultipleSites === "No") {
        setSiteRows(prev => {
          if (prev[0]?.siteShortName) return prev;
          const next = [...prev];
          if (next[0]) {
            next[0] = { ...next[0], siteShortName: cmoShortName };
          }
          return next;
        });
      }
    }, [cmoHasMultipleSites, cmoShortName, editMode]);

    const handleCmoHasMultipleSitesChange = (e) => {
      const value = e.target.value;
      setCmoHasMultipleSites(value);
      if (errors.cmoHasMultipleSites) {
        setErrors((prev) => ({ ...prev, cmoHasMultipleSites: "" }));
      }
      if (value === "No" && siteRows.length > 1) {
        setSiteRows((prev) => [prev[0] || createEmptySite()]);
      }
    };



    const handleSiteFieldChange = (siteIndex, field, value) => {
      setErrors((prev) => {
        const variable = `site_${siteIndex}_${field}`;
        const { [variable]: removed, ...rest } = prev;
        return rest;
      });
      if (field === "siteHasMultiplePlants" && value === "No") {
        setSiteRows((prev) => {
          const next = [...prev];
          const site = { ...next[siteIndex] };
          const currentPlants = Array.isArray(site.manufacturingPlantDataList)
            ? site.manufacturingPlantDataList
            : site.manufacturingPlantDataList
              ? [site.manufacturingPlantDataList]
              : [];
          const firstPlant = currentPlants.length > 0 ? { ...currentPlants[0] } : { ...createEmptyPlant() };
          // Auto-populate mfg plant short name from site short name when site has no multiple plants
          // Only populate if mfg plant short name is not already set
          firstPlant.mfgPlantShortName = firstPlant.mfgPlantShortName || site.siteShortName;
          site.siteHasMultiplePlants = value;
          site.manufacturingPlantDataList = [firstPlant];
          next[siteIndex] = site;
          return next;
        });
      } else if (field === "siteShortName") {
        setSiteRows((prev) =>
          prev.map((row, i) => {
            if (i !== siteIndex) return row;
            const updatedRow = { ...row, [field]: value };
            // If site has no multiple plants, auto-populate mfg plant short name only if not already set
            if (row.siteHasMultiplePlants === "No") {
              const plants = [...(updatedRow.manufacturingPlantDataList || [])];
              if (plants[0] && !plants[0].mfgPlantShortName) {
                plants[0] = { ...plants[0], mfgPlantShortName: value };
              }
              updatedRow.manufacturingPlantDataList = plants;
            }
            return updatedRow;
          })
        );
      } else {
        setSiteRows((prev) =>
          prev.map((row, i) =>
            i === siteIndex ? { ...row, [field]: value } : row
          )
        );
      }
    };

    const handleSiteFieldBlur = (siteIndex, field) => {
      setSiteRows(prev => {
        const next = [...prev];
        const site = { ...next[siteIndex] };
        const trimmed = String(site[field] || "").trim();
        site[field] = trimmed;
        if (field === "siteShortName" && site.siteHasMultiplePlants === "No") {
          const plants = [...site.manufacturingPlantDataList];
          if (plants[0] && !plants[0].mfgPlantShortName) plants[0] = { ...plants[0], mfgPlantShortName: trimmed };
          site.manufacturingPlantDataList = plants;
        }
        next[siteIndex] = site;
        return next;
      });
    };

    const handlePlantFieldBlur = (siteIndex, plantIndex, field) => {
      setSiteRows(prev => {
        const next = [...prev];
        const site = { ...next[siteIndex] };
        const plants = [...site.manufacturingPlantDataList];
        plants[plantIndex] = { ...plants[plantIndex], [field]: String(plants[plantIndex][field] || "").trim() };
        site.manufacturingPlantDataList = plants;
        next[siteIndex] = site;
        return next;
      });
    };

    const handlePlantFieldChange = (siteIndex, plantIndex, field, value) => {
      setErrors((prev) => {
        const variable = `site_${siteIndex}_plant_${plantIndex}_${field}`;
        const { [variable]: removed, ...rest } = prev;
        return rest;
      });
      setSiteRows((prev) => {
        const next = [...prev];
        const site = { ...next[siteIndex] };
        const plants = [...site.manufacturingPlantDataList];
        plants[plantIndex] = { ...plants[plantIndex], [field]: value };
        site.manufacturingPlantDataList = plants;
        next[siteIndex] = site;
        return next;
      });
    };

    const handleAddNewSite = () => {
      if (cmoHasMultipleSites !== "Yes") return;
      setSiteRows((prev) => [...prev, createEmptySite()]);
    };

    const handleDeleteSite = (siteIndex) => {
      setErrors((prev) => {
        const prefix = `site_${siteIndex}_`;
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (key.startsWith(prefix)) delete updated[key];
        });
        return updated;
      });
      if (cmoHasMultipleSites !== "Yes" || siteRows.length <= 1) return;
      setSiteRows((prev) => prev.filter((_, i) => i !== siteIndex));
    };

    const handleAddManufacturingPlant = (siteIndex) => {
      setSiteRows((prev) => {
        const next = [...prev];
        const site = { ...next[siteIndex] };
        site.manufacturingPlantDataList = [
          ...site.manufacturingPlantDataList,
          createEmptyPlant(),
        ];
        next[siteIndex] = site;
        return next;
      });
    };

    const handleDeleteManufacturingPlant = (siteIndex, plantIndex) => {
      setErrors((prev) => {
        const prefix = `site_${siteIndex}_plant_${plantIndex}_`;
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (key.startsWith(prefix)) delete updated[key];
        });
        return updated;
      });
      setSiteRows((prev) => {
        const next = [...prev];
        const site = { ...next[siteIndex] };
        const plants = site.manufacturingPlantDataList.filter((_, i) => i !== plantIndex);
        if (plants.length === 0) return prev;
        site.manufacturingPlantDataList = plants;
        next[siteIndex] = site;
        return next;
      });
    };

    const validate = (addToErrorState) => {
      const newErrors = {};
      if (!cmoHasMultipleSites) {
        newErrors.cmoHasMultipleSites = "CMO has multiple sites is required";
      }
      siteRows.forEach((site, s) => {
        if (!site.siteID || !String(site.siteID).trim()) {
          newErrors[`site_${s}_siteID`] = "Site ID is required";
        } else if (!/^[A-Za-z0-9\-_]{1,20}$/.test(site.siteID)) {
          newErrors[`site_${s}_siteID`] =
            "Site ID must be alphanumeric, hyphens and underscores only, max 20 characters";
        }
        if (!site.siteShortName || !String(site.siteShortName).trim()) {
          newErrors[`site_${s}_siteShortName`] = "Site Short Name is required";
        } else if (!/^[A-Za-zÀ-ÿ0-9\s\-_]{1,30}$/.test(site.siteShortName)) {
          newErrors[`site_${s}_siteShortName`] =
            "Site Short Name must be alphanumeric, spaces, hyphens and underscores only, max 30 characters";
        }
        if (!site.siteHasMultiplePlants) {
          newErrors[`site_${s}_siteHasMultiplePlants`] =
            "Site has multiple Manufacturing Plants is required";
        }
        site.manufacturingPlantDataList.forEach((plant, p) => {
          if (!plant.mfgPlantID || !String(plant.mfgPlantID).trim()) {
            newErrors[`site_${s}_plant_${p}_mfgPlantID`] =
              "Manufacturing Plant ID is required";
          }
          if (!plant.manufacturingPlantIdSameAsASL) {
            newErrors[`site_${s}_plant_${p}_manufacturingPlantIdSameAsASL`] =
              "Manufacturing Plant ID same as ASL Number is required";
          }
          if (!plant.mfgPlantShortName || !String(plant.mfgPlantShortName).trim()) {
            newErrors[`site_${s}_plant_${p}_mfgPlantShortName`] =
              "Manufacturing Plant Short Name is required";
          }
        });
      });

      // Uniqueness: no siteID, siteShortName, mfgPlantID or mfgPlantShortName should be duplicated
      const siteIDs = siteRows.map((site, idx) => ({ value: String((site.siteID || '').trim()).toLowerCase(), siteIndex: idx }));
      const siteShortNames = siteRows.map((site, idx) => ({ value: String((site.siteShortName || '').trim()).toLowerCase(), siteIndex: idx }));
      const mfgPlantIDs = [];
      const mfgPlantShortNames = [];
      siteRows.forEach((site, s) => {
        (site.manufacturingPlantDataList || []).forEach((plant, p) => {
          mfgPlantIDs.push({ value: String((plant.mfgPlantID || '').trim()).toLowerCase(), siteIndex: s, plantIndex: p });
          mfgPlantShortNames.push({ value: String((plant.mfgPlantShortName || '').trim()).toLowerCase(), siteIndex: s, plantIndex: p });
        });
      });

      const findDuplicateKeys = (items) => {
        const byValue = {};
        items.forEach((item) => {
          if (!item.value) return;
          if (!byValue[item.value]) byValue[item.value] = [];
          byValue[item.value].push(item);
        });
        return Object.keys(byValue).filter((v) => byValue[v].length > 1).flatMap((v) => byValue[v]);
      };

      findDuplicateKeys(siteIDs).forEach(({ siteIndex }) => {
        newErrors[`site_${siteIndex}_siteID`] = "Site ID must be unique across all sites";
      });
      findDuplicateKeys(siteShortNames).forEach(({ siteIndex }) => {
        newErrors[`site_${siteIndex}_siteShortName`] = "Site Short Name must be unique across all sites";
      });
      findDuplicateKeys(mfgPlantIDs).forEach(({ siteIndex, plantIndex }) => {
        newErrors[`site_${siteIndex}_plant_${plantIndex}_mfgPlantID`] = "Manufacturing Plant ID must be unique across all plants";
      });
      findDuplicateKeys(mfgPlantShortNames).forEach(({ siteIndex, plantIndex }) => {
        newErrors[`site_${siteIndex}_plant_${plantIndex}_mfgPlantShortName`] = "Manufacturing Plant Short Name must be unique across all plants";
      });

      if (addToErrorState) {

        setErrors(newErrors);
        setSubmitted(true);
      }
      return Object.keys(newErrors).length === 0;
    };

    // Effect to detect changes for update scenario
    useEffect(() => {
      let originalData = { ...originalSiteDetails }

      if (!originalData) return;

      if (originalData?.cmoSites && Array.isArray(originalData.cmoSites) && originalData.cmoSites.length > 0) {
        const originalNormalizedSites = originalData.cmoSites.map((site) => {
          const plants = site?.manufacturingPlantDataList && Array.isArray(site.manufacturingPlantDataList) && site.manufacturingPlantDataList.length > 0
            ? site.manufacturingPlantDataList.map((plant) => ({
              ...plant?.mfgPlantDetails,
              manufacturingPlantIdSameAsASL:
                plant?.mfgPlantDetails?.mfgPlantID && plant?.mfgPlantDetails?.aslNumber && String(plant?.mfgPlantDetails.aslNumber).trim() === String(plant?.mfgPlantDetails.mfgPlantID).trim()
                  ? "Yes"
                  : "No",
            }))
            : [createEmptyPlant()];

          return {
            ...site?.siteDetails,
            siteHasMultiplePlants: plants.length > 1 ? "Yes" : "No",
            manufacturingPlantDataList: plants,
          };
        });

        const sitesMatch = (a, b) => {
          if (!a || !b) return false;
          const siteIdMatch = String(a.siteID || "").trim() === String(b.siteID || "").trim();
          const siteNameMatch = String(a.siteShortName || "").trim() === String(b.siteShortName || "").trim();
          const plantsA = a.manufacturingPlantDataList || [];
          const plantsB = b.manufacturingPlantDataList || [];
          if (plantsA.length !== plantsB.length) return false;
          const plantsMatch = plantsA.every((pa, i) => {
            const pb = plantsB[i] || {};
            return (
              String(pa?.mfgPlantID || "").trim() === String(pb?.mfgPlantID || "").trim() &&
              String(pa?.mfgPlantShortName || "").trim() === String(pb?.mfgPlantShortName || "").trim() &&
              String(pa?.aslNumber ?? "").trim() === String(pb?.aslNumber ?? "").trim()
            );
          });
          return siteIdMatch && siteNameMatch && plantsMatch;
        };

        const hasSiteRowsChanged =
          siteRows.length !== originalNormalizedSites.length ||
          siteRows.some((row, i) => !sitesMatch(row, originalNormalizedSites[i]));

        const originalCmoHasMultipleSites =
          typeof originalData?.cmoHasMultipleSites === "boolean"
            ? originalData.cmoHasMultipleSites === true
              ? "Yes"
              : "No"
            : "";
        const cmoHasMultipleSitesChanged =
          originalCmoHasMultipleSites !== "" &&
          cmoHasMultipleSites !== "" &&
          originalCmoHasMultipleSites !== cmoHasMultipleSites;

        const siteFieldsChanged = cmoHasMultipleSitesChanged || hasSiteRowsChanged;
        setSiteDetailsChanged(siteFieldsChanged);
      } else if (Array.isArray(originalData.cmoSites) && originalData.cmoSites.length === 0 && siteRows.length > 0 && cmoHasMultipleSites !== '') {
        setSiteDetailsChanged(true);
        // if (validate(false)) {
        //   console.log("true")
        //   setSiteDetailsChanged(true);
        // }else{
        //   console.log("false.....")
        //   setSiteDetailsChanged(false);
        // }
      } else {
        setSiteDetailsChanged(false);
      }
    }, [originalSiteDetails, siteRows, cmoHasMultipleSites]);


    function mergeCMOSites(updatedSites = [], originalSites = []) {
      const safe = (arr) => (Array.isArray(arr) ? arr : []);


      const normalizedOriginal = safe(originalSites).map(site => ({
        siteDetails: site?.siteDetails || {},
        manufacturingPlantDataList: safe(site?.manufacturingPlantDataList).map(
          p => (p?.mfgPlantDetails != null ? { ...p.mfgPlantDetails } : { ...p })
        )
      }));

      const originalBySiteID = new Map();
      const originalByCMOSiteID = new Map();
      normalizedOriginal.forEach(site => {
        const id = site?.siteDetails?.siteID;
        const cmoId = site?.siteDetails?.collabrixCMOSiteID;
        if (id != null) originalBySiteID.set(id, site);
        if (cmoId != null) originalByCMOSiteID.set(cmoId, site);
      });

      const result = [];

      const flattenPlant = (p) => (p?.mfgPlantDetails != null ? { ...p.mfgPlantDetails } : { ...p });

      safe(updatedSites).forEach(updatedSite => {
        const siteDetails = updatedSite?.siteDetails || {};
        const siteID = siteDetails.siteID;
        const collabrixCMOSiteID = siteDetails.collabrixCMOSiteID;
        const originalSite =
          originalBySiteID.get(siteID) ?? (collabrixCMOSiteID != null ? originalByCMOSiteID.get(collabrixCMOSiteID) : undefined);

        const mergedPlants = [];
        const updatedPlantIds = new Set(
          safe(updatedSite.manufacturingPlantDataList)
            .map(p => flattenPlant(p)?.collabrixMfgPlantID)
            .filter(id => id != null)
        );
        const originalPlants = safe(originalSite?.manufacturingPlantDataList).filter(
          p => (p?.mfgPlantDetails != null ? p.mfgPlantDetails : p)?.collabrixMfgPlantID != null
        );

        safe(updatedSite.manufacturingPlantDataList).forEach(plant => {
          const flatPlant = flattenPlant(plant);
          const mfgPlantId = flatPlant?.collabrixMfgPlantID;
          mergedPlants.push({
            collabrixCMOSiteID: flatPlant?.collabrixCMOSiteID ?? collabrixCMOSiteID,
            collabrixMfgPlantID: mfgPlantId,
            mfgPlantID: flatPlant?.mfgPlantID,
            mfgPlantShortName: flatPlant?.mfgPlantShortName,
            aslNumber: flatPlant?.aslNumber,
            markForDelete: mfgPlantId != null ? false : false
          });
        });

        originalPlants.forEach(plant => {
          const flat = flattenPlant(plant);
          const id = flat?.collabrixMfgPlantID;
          if (id != null && !updatedPlantIds.has(id)) {
            mergedPlants.push({
              collabrixCMOSiteID: flat?.collabrixCMOSiteID ?? collabrixCMOSiteID,
              collabrixMfgPlantID: flat?.collabrixMfgPlantID,
              mfgPlantID: flat?.mfgPlantID,
              mfgPlantShortName: flat?.mfgPlantShortName,
              aslNumber: flat?.aslNumber,
              markForDelete: id != null
            });
          }
        });

        const origSiteDetails = originalSite?.siteDetails || {};
        const mergedSiteDetails = {
          ...origSiteDetails,
          ...siteDetails,
          markForDelete: false
        };

        result.push({
          siteDetails: mergedSiteDetails,
          manufacturingPlantDataList: mergedPlants
        });

        if (siteID != null) originalBySiteID.delete(siteID);
        if (originalSite?.siteDetails?.collabrixCMOSiteID != null) {
          originalByCMOSiteID.delete(originalSite.siteDetails.collabrixCMOSiteID);
        }
      });

      // ===== sites only in original (deleted in UI) → site and all plants markForDelete: true =====
      const remainingOriginalSites = normalizedOriginal.filter(
        site =>
          !result.some(
            r =>
              r.siteDetails?.siteID === site.siteDetails?.siteID ||
              r.siteDetails?.collabrixCMOSiteID === site.siteDetails?.collabrixCMOSiteID
          )
      );
      remainingOriginalSites.forEach(site => {
        const siteId = site?.siteDetails?.collabrixCMOSiteID;
        result.push({
          siteDetails: {
            ...site.siteDetails,
            markForDelete: siteId != null
          },
          manufacturingPlantDataList: safe(site.manufacturingPlantDataList).map(p => {
            const flat = flattenPlant(p);
            const mfgPlantId = flat?.collabrixMfgPlantID ?? p?.collabrixMfgPlantID;
            return { ...flat, ...p, markForDelete: mfgPlantId != null };
          })
        });
      });

      return result;
    }


    useImperativeHandle(ref, () => ({
      getFormData: () => {
        let originalData = { ...originalSiteDetails }

        const cmoHasMultipleSitesBool = cmoHasMultipleSites === "Yes";
        let cmoSites = siteRows.map((val) => ({
          siteDetails: {
            collabrixCMOSiteID: val?.collabrixCMOSiteID,
            siteID: val.siteID,
            siteShortName: val.siteShortName,
          },
          manufacturingPlantDataList: (val.manufacturingPlantDataList || []).map((plant) => ({
            collabrixCMOSiteID: plant?.collabrixCMOSiteID,
            collabrixMfgPlantID: plant?.collabrixMfgPlantID,
            mfgPlantID: plant.mfgPlantID,
            mfgPlantShortName: plant.mfgPlantShortName,
            aslNumber: plant.manufacturingPlantIdSameAsASL === "Yes" ? plant.mfgPlantID : "",
          }))
        }))

        if (originalData?.cmoSites && Array.isArray(originalData.cmoSites) && originalData.cmoSites.length > 0) {
          const result = mergeCMOSites(cmoSites, originalData.cmoSites);
          const plantMap = new Map();

          originalData?.cmoSites?.forEach((site) => {
            site?.manufacturingPlantDataList?.forEach((mfg) => {
              const key = `${mfg?.mfgPlantDetails?.collabrixCMOSiteID}_${mfg?.mfgPlantDetails?.collabrixMfgPlantID}`;
              plantMap.set(key, mfg);
            });
          });

          cmoSites = result.map((site) => ({
            ...site,
            manufacturingPlantDataList: site.manufacturingPlantDataList.map((plant) => {

              const key = `${plant?.collabrixCMOSiteID}_${plant?.collabrixMfgPlantID}`;
              const matchedPlant = plantMap.get(key);

              if (matchedPlant) {
                return {
                  ...matchedPlant,
                  mfgPlantDetails: { ...plant },
                };
              }

              return {
                supplyTypeIDList: [],
                pickupAddress: {},
                deliveryAddress: {},
                lineDetailsList: [],
                plantTypeIDList: [],
                mainAddress: {},
                drugTypeIDList: [],
                facilityTypeIDList: [],
                mfgPlantDetails: { ...plant },
              };
            }),
          }));

          // cmoSites = result.map((val) => {
          //   return {
          //     ...val, manufacturingPlantDataList: val.manufacturingPlantDataList.map((val1) => {
          //       // let mfgData = {}
          //       console.log(val1)
          //       if (val1?.collabrixMfgPlantID && val1?.collabrixCMOSiteID) {
          //         console.log(originalData.cmoSites)
          //         originalData.cmoSites.forEach(element => {
          //           element.manufacturingPlantDataList.forEach(mfg => {
          //             if (mfg?.mfgPlantDetails?.collabrixMfgPlantID &&
          //               mfg?.mfgPlantDetails?.collabrixCMOSiteID &&
          //               mfg?.mfgPlantDetails?.collabrixMfgPlantID === val1?.collabrixMfgPlantID &&
          //               mfg?.mfgPlantDetails?.collabrixCMOSiteID === val1?.collabrixCMOSiteID) {
          //               // mfgData = { ...mfg }
          //               return {
          //                 ...mfg,
          //                 mfgPlantDetails: { ...val1 },
          //               }
          //             }
          //             // else {
          //             //   mfgData = {
          //             //     supplyTypeIDList: [],
          //             //     pickupAddress: {},
          //             //     deliveryAddress: {},
          //             //     lineDetailsList: [],
          //             //     plantTypeIDList: [],
          //             //     mainAddress: {},
          //             //     drugTypeIDList: [],
          //             //     facilityTypeIDList: [],
          //             //   }
          //             // }
          //           });
          //         });
          //       }
          //       return {
          //         supplyTypeIDList: [],
          //         pickupAddress: {},
          //         deliveryAddress: {},
          //         lineDetailsList: [],
          //         plantTypeIDList: [],
          //         mainAddress: {},
          //         drugTypeIDList: [],
          //         facilityTypeIDList: [],
          //         mfgPlantDetails: { ...val1 },
          //       }
          //     })
          //   }
          // })
        } else {
          cmoSites = cmoSites.map((val) => ({
            ...val, manufacturingPlantDataList: val.manufacturingPlantDataList.map((val1) => ({
              mfgPlantDetails: { ...val1 },
              supplyTypeIDList: [],
              pickupAddress: {},
              deliveryAddress: {},
              lineDetailsList: [],
              plantTypeIDList: [],
              mainAddress: {},
              drugTypeIDList: [],
              facilityTypeIDList: [],
            }))
          }));
        }

        return { cmoHasMultipleSites: cmoHasMultipleSitesBool, cmoSites: cmoSites }
      },
      getPayload: () => {
        let originalData = { ...originalSiteDetails }

        const cmoHasMultipleSitesBool = cmoHasMultipleSites === "Yes";
        let payload = {
          vendorMosymphonyID,
          brandOwnerMosymphonyID: brandOwnerMosymphonyID,
          updateCMOSiteDetailsFlag: true,
          updatePlantDetailsFlag: true,
          vendorPlantDetails: {
            vendorMosymphonyID,
            cmoHasMultipleSites: cmoHasMultipleSitesBool,
          },
          cmoSites: siteRows.map((val) => ({
            siteDetails: {
              collabrixCMOSiteID: val?.collabrixCMOSiteID,
              siteID: val.siteID,
              siteShortName: val.siteShortName,
            },
            manufacturingPlantDataList: (val.manufacturingPlantDataList || []).map((plant) => ({
              collabrixCMOSiteID: plant?.collabrixCMOSiteID,
              collabrixMfgPlantID: plant?.collabrixMfgPlantID,
              mfgPlantID: plant.mfgPlantID,
              mfgPlantShortName: plant.mfgPlantShortName,
              aslNumber: plant.manufacturingPlantIdSameAsASL === "Yes" ? plant.mfgPlantID : "",
            }))
          }))
        };

        if (originalData?.cmoSites && Array.isArray(originalData.cmoSites) && originalData.cmoSites.length > 0) {
          const result = mergeCMOSites(payload.cmoSites, originalData.cmoSites);
          const plantMap = new Map();

          originalData?.cmoSites?.forEach((site) => {
            site?.manufacturingPlantDataList?.forEach((mfg) => {
              const key = `${mfg?.mfgPlantDetails?.collabrixCMOSiteID}_${mfg?.mfgPlantDetails?.collabrixMfgPlantID}`;
              plantMap.set(key, mfg);
            });
          });

          payload.cmoSites = result.map((site) => ({
            ...site,
            manufacturingPlantDataList: site.manufacturingPlantDataList.map((plant) => {

              const key = `${plant?.collabrixCMOSiteID}_${plant?.collabrixMfgPlantID}`;
              const matchedPlant = plantMap.get(key);

              if (matchedPlant) {
                return {
                  ...matchedPlant,
                  mfgPlantDetails: { ...plant },
                };
              }

              return {
                supplyTypeIDList: [],
                pickupAddress: {},
                deliveryAddress: {},
                lineDetailsList: [],
                plantTypeIDList: [],
                mainAddress: {},
                drugTypeIDList: [],
                facilityTypeIDList: [],
                mfgPlantDetails: { ...plant },
              };
            }),
          }));

          // payload.cmoSites = result.map((val) => {
          //   return {
          //     ...val, manufacturingPlantDataList: val.manufacturingPlantDataList.map((val1) => {
          //       // let mfgData = {}
          //       console.log(val1)
          //       if (val1?.collabrixMfgPlantID && val1?.collabrixCMOSiteID) {
          //         console.log(originalData.cmoSites)
          //         originalData.cmoSites.forEach(element => {
          //           element.manufacturingPlantDataList.forEach(mfg => {
          //             if (mfg?.mfgPlantDetails?.collabrixMfgPlantID &&
          //               mfg?.mfgPlantDetails?.collabrixCMOSiteID &&
          //               mfg?.mfgPlantDetails?.collabrixMfgPlantID === val1?.collabrixMfgPlantID &&
          //               mfg?.mfgPlantDetails?.collabrixCMOSiteID === val1?.collabrixCMOSiteID) {
          //               console.log(mfg)
          //               // mfgData = { ...mfg }
          //               return {
          //                 ...mfg,
          //                 mfgPlantDetails: { ...val1 },
          //               }
          //             }
          //             // else {
          //             //   mfgData = {
          //             //     supplyTypeIDList: [],
          //             //     pickupAddress: {},
          //             //     deliveryAddress: {},
          //             //     lineDetailsList: [],
          //             //     plantTypeIDList: [],
          //             //     mainAddress: {},
          //             //     drugTypeIDList: [],
          //             //     facilityTypeIDList: [],
          //             //   }
          //             // }
          //           });
          //         });
          //       }
          //       return {
          //         supplyTypeIDList: [],
          //         pickupAddress: {},
          //         deliveryAddress: {},
          //         lineDetailsList: [],
          //         plantTypeIDList: [],
          //         mainAddress: {},
          //         drugTypeIDList: [],
          //         facilityTypeIDList: [],
          //         mfgPlantDetails: { ...val1 },
          //       }
          //     })
          //   }
          // })
        } else {
          payload.cmoSites = payload.cmoSites.map((val) => ({
            ...val, manufacturingPlantDataList: val.manufacturingPlantDataList.map((val1) => ({
              mfgPlantDetails: { ...val1 },
              supplyTypeIDList: [],
              pickupAddress: {},
              deliveryAddress: {},
              lineDetailsList: [],
              plantTypeIDList: [],
              mainAddress: {},
              drugTypeIDList: [],
              facilityTypeIDList: [],
            }))
          }));
        }
        return payload
        // const vendorPlantDetails = [];
        // siteRows.forEach((site) => {
        //   site.siteManufacturingPlants.forEach((plant) => {
        //     vendorPlantDetails.push({
        //       siteID: site.siteID,
        //       siteIDShortName: site.siteShortName,
        //       manufacturingPlantId: plant.mfgPlantID,
        //       manufacturingPlantIdSameAsASL: plant.manufacturingPlantIdSameAsASL,
        //       manufacturingPlantShortName: plant.mfgPlantShortName,
        //     });
        //   });
        // });
        // return {
        //   vendorMosymphonyID,
        //   brandOwnerMosymphonyID,
        //   updateCMOSiteDetailsFlag: true,
        //   cmoHasMultipleSites,
        //   siteRows
        // };
      },
      validateForm: () => validate(true),
      hasChanges: () => true,
      resetForm: () => {
        setCmoHasMultipleSites(resolvedInitial.cmoHasMultipleSites || "No");
        setSiteRows(resolvedInitial.siteRows || [createEmptySite()]);
        setErrors({});
        setSubmitted(false);
      },
    }));

    return (
      <div className="cmo-stepper-wrapper site-details-wrapper">
        <div className="stepper-container">
          <form autoComplete="off">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="cmoHasMultipleSites">
                    CMO has multiple sites {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{cmoHasMultipleSites || "—"}</Typography>
                  ) : (
                    <Select
                      id="cmoHasMultipleSites"
                      name="cmoHasMultipleSites"
                      value={cmoHasMultipleSites || ""}
                      onChange={handleCmoHasMultipleSitesChange}
                      variant="outlined"
                      fullWidth
                      required
                      displayEmpty
                      error={submitted && Boolean(errors.cmoHasMultipleSites)}
                      InputProps={{ style: commonInlineStyles.input }}
                      sx={inputFieldStyles}
                      inputProps={commonInlineStyles.inputProps}
                      style={commonInlineStyles.input}
                      helperText={
                        submitted && errors.cmoHasMultipleSites
                          ? errors.cmoHasMultipleSites
                          : ""
                      }
                    >
                      <MenuItem value="" disabled>
                        Select from the list
                      </MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={12}>
                <Typography component="label" style={{ display: "block", marginBottom: 8 }}>
                  Site and Manufacturing Plant Details
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  style={{
                    marginBottom: 16,
                    opacity: !cmoHasMultipleSites ? 0.6 : 1,
                    pointerEvents: !cmoHasMultipleSites ? "none" : "auto",
                  }}
                >
                  <Table size="small" stickyHeader style={{ tableLayout: "auto", width: "100%" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableHeader}>
                          Site ID&nbsp;{editMode && <span style={{ color: "red" }}>*</span>}
                        </TableCell>
                        <TableCell className={classes.tableHeader}>
                          Site Short Name&nbsp;{editMode && <span style={{ color: "red" }}>*</span>}
                        </TableCell>
                        <TableCell className={classes.tableHeader}>
                          Site has multiple Manufacturing Plants&nbsp;{editMode && <span style={{ color: "red" }}>*</span>}
                        </TableCell>
                        <TableCell className={classes.tableHeader}>
                          Manufacturing Plant ID&nbsp;{editMode && <span style={{ color: "red" }}>*</span>}
                        </TableCell>
                        <TableCell className={classes.tableHeader}>
                          Manufacturing Plant ID same as ASL Number&nbsp;{editMode && <span style={{ color: "red" }}>*</span>}
                        </TableCell>
                        <TableCell className={classes.tableHeader}>
                          Manufacturing Plant Short Name&nbsp;{editMode && <span style={{ color: "red" }}>*</span>}
                        </TableCell>
                        {editMode && cmoHasMultipleSites === "Yes" && (
                          <TableCell className={classes.tableHeader} align="center">
                            Action
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {siteRows.map((site, siteIndex) => (
                        <React.Fragment key={siteIndex}>
                          {site.manufacturingPlantDataList.map((plant, plantIndex) => (
                            <TableRow key={`${siteIndex}-${plantIndex}`}>
                              {plantIndex === 0 ? (
                                <>
                                  <TableCell className={classes.tableCell} rowSpan={site.manufacturingPlantDataList.length}>
                                    {!editMode ? (
                                      <Typography>{site.siteID || "—"}</Typography>
                                    ) : (
                                      <TextField
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        placeholder="Enter Value Here"
                                        value={site.siteID || ""}
                                        onChange={(e) =>
                                          handleSiteFieldChange(siteIndex, "siteID", e.target.value)
                                        }
                                        onBlur={() => handleSiteFieldBlur(siteIndex, "siteID")}
                                        inputProps={{
                                          maxLength: 20,
                                          ...commonInlineStyles.inputProps,
                                        }}
                                        onInput={(e) => {
                                          e.target.value = e.target.value
                                            .replace(/[^a-zA-Z0-9\-_]/g, "")
                                            .replace(/(-{2,}|_{2,})/g, (m) => m[0]);
                                        }}
                                        error={submitted && Boolean(errors[`site_${siteIndex}_siteID`])}
                                        helperText={
                                          submitted && errors[`site_${siteIndex}_siteID`]
                                            ? errors[`site_${siteIndex}_siteID`]
                                            : ""
                                        }
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell className={classes.tableCell} rowSpan={site.manufacturingPlantDataList.length}>
                                    {!editMode ? (
                                      <Typography>{site.siteShortName || "—"}</Typography>
                                    ) : (
                                      <TextField
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        placeholder="Enter Value Here"
                                        value={site.siteShortName || ""}
                                        onChange={(e) =>
                                          handleSiteFieldChange(siteIndex, "siteShortName", e.target.value)
                                        }
                                        onBlur={() => handleSiteFieldBlur(siteIndex, "siteShortName")}
                                        inputProps={{
                                          maxLength: 30,
                                          ...commonInlineStyles.inputProps,
                                        }}
                                        onInput={(e) => {
                                          e.target.value = e.target.value
                                            .replace(/[^a-zA-ZÀ-ÿ0-9\s\-_]/g, "")
                                            .replace(/(-{2,}|_{2,}|\s{2,})/g, (m) => m[0]);
                                        }}
                                        error={submitted && Boolean(errors[`site_${siteIndex}_siteShortName`])}
                                        helperText={
                                          submitted && errors[`site_${siteIndex}_siteShortName`]
                                            ? errors[`site_${siteIndex}_siteShortName`]
                                            : ""
                                        }
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell className={classes.tableCell} rowSpan={site.manufacturingPlantDataList.length}>
                                    {!editMode ? (
                                      <Typography>{site.siteHasMultiplePlants || "—"}</Typography>
                                    ) : (
                                      <>
                                        <Select
                                          fullWidth
                                          size="small"
                                          variant="outlined"
                                          value={site.siteHasMultiplePlants || ""}
                                          onChange={(e) =>
                                            handleSiteFieldChange(
                                              siteIndex,
                                              "siteHasMultiplePlants",
                                              e.target.value
                                            )
                                          }
                                          displayEmpty
                                          inputProps={commonInlineStyles.inputProps}
                                          error={
                                            submitted && Boolean(errors[`site_${siteIndex}_siteHasMultiplePlants`])
                                          }
                                          helperText={
                                            submitted && errors[`site_${siteIndex}_siteHasMultiplePlants`]
                                              ? errors[`site_${siteIndex}_siteHasMultiplePlants`]
                                              : ""
                                          }
                                        >
                                          <MenuItem value="" disabled>
                                            Select an option
                                          </MenuItem>
                                          <MenuItem value="Yes">Yes</MenuItem>
                                          <MenuItem value="No">No</MenuItem>
                                        </Select>
                                      </>
                                    )}
                                  </TableCell>
                                </>
                              ) : null}
                              <TableCell className={classes.tableCell}>
                                {!editMode ? (
                                  <Typography>{plant.mfgPlantID || "—"}</Typography>
                                ) : (
                                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      variant="outlined"
                                      placeholder="Enter Value Here"
                                      value={plant.mfgPlantID || ""}
                                      onChange={(e) =>
                                        handlePlantFieldChange(
                                          siteIndex,
                                          plantIndex,
                                          "mfgPlantID",
                                          e.target.value
                                        )
                                      }
                                      onBlur={() => handlePlantFieldBlur(siteIndex, plantIndex, "mfgPlantID")}
                                      inputProps={commonInlineStyles.inputProps}
                                      onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, "");
                                      }}
                                      error={
                                        submitted &&
                                        Boolean(
                                          errors[`site_${siteIndex}_plant_${plantIndex}_mfgPlantID`]
                                        )
                                      }
                                      helperText={
                                        submitted &&
                                          errors[`site_${siteIndex}_plant_${plantIndex}_mfgPlantID`]
                                          ? errors[`site_${siteIndex}_plant_${plantIndex}_mfgPlantID`]
                                          : ""
                                      }
                                    />
                                    {site.siteHasMultiplePlants === "Yes" && plantIndex === 0 ? (
                                      <IconButton
                                        size="small"
                                        onClick={() => handleAddManufacturingPlant(siteIndex)}
                                        aria-label="Add manufacturing plant"
                                        color="primary"
                                      >
                                        <AddCircleOutlineIcon fontSize="small" />
                                      </IconButton>
                                    ) : site.siteHasMultiplePlants === "Yes" && plantIndex > 0 ? (
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteManufacturingPlant(siteIndex, plantIndex)}
                                        aria-label="Delete manufacturing plant"
                                        style={{ color: "#d32f2f" }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    ) : null}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className={classes.tableCell}>
                                {!editMode ? (
                                  <Typography>{plant.manufacturingPlantIdSameAsASL || "—"}</Typography>
                                ) : (
                                  <>
                                    <Select
                                      fullWidth
                                      size="small"
                                      variant="outlined"
                                      value={plant.manufacturingPlantIdSameAsASL || ""}
                                      onChange={(e) =>
                                        handlePlantFieldChange(
                                          siteIndex,
                                          plantIndex,
                                          "manufacturingPlantIdSameAsASL",
                                          e.target.value
                                        )
                                      }
                                      displayEmpty
                                      inputProps={commonInlineStyles.inputProps}
                                      error={
                                        submitted &&
                                        Boolean(
                                          errors[
                                          `site_${siteIndex}_plant_${plantIndex}_manufacturingPlantIdSameAsASL`
                                          ]
                                        )
                                      }
                                      helperText={
                                        submitted &&
                                          errors[
                                          `site_${siteIndex}_plant_${plantIndex}_manufacturingPlantIdSameAsASL`
                                          ]
                                          ? errors[
                                          `site_${siteIndex}_plant_${plantIndex}_manufacturingPlantIdSameAsASL`
                                          ]
                                          : ""
                                      }
                                    >
                                      <MenuItem value="" disabled>
                                        Select an option
                                      </MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                      <MenuItem disabled value="No">No</MenuItem>
                                    </Select>
                                  </>
                                )}
                              </TableCell>
                              <TableCell className={classes.tableCell}>
                                {!editMode ? (
                                  <Typography>{plant.mfgPlantShortName || "—"}</Typography>
                                ) : (
                                  <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    placeholder="Enter Value Here"
                                    value={plant.mfgPlantShortName || ""}
                                    onChange={(e) =>
                                      handlePlantFieldChange(
                                        siteIndex,
                                        plantIndex,
                                        "mfgPlantShortName",
                                        e.target.value
                                      )
                                    }
                                    onBlur={() => handlePlantFieldBlur(siteIndex, plantIndex, "mfgPlantShortName")}
                                    inputProps={commonInlineStyles.inputProps}
                                    error={
                                      submitted &&
                                      Boolean(
                                        errors[
                                        `site_${siteIndex}_plant_${plantIndex}_mfgPlantShortName`
                                        ]
                                      )
                                    }
                                    helperText={
                                      submitted &&
                                        errors[
                                        `site_${siteIndex}_plant_${plantIndex}_mfgPlantShortName`
                                        ]
                                        ? errors[
                                        `site_${siteIndex}_plant_${plantIndex}_mfgPlantShortName`
                                        ]
                                        : ""
                                    }
                                  />
                                )}
                              </TableCell>
                              {editMode && cmoHasMultipleSites === "Yes" && (
                                <TableCell className={classes.tableCell} align="center">
                                  {plantIndex === 0 ? (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteSite(siteIndex)}
                                      aria-label="Delete site"
                                      style={{ color: "#d32f2f" }}
                                      disabled={siteRows.length <= 1}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  ) : null}
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {editMode && cmoHasMultipleSites === "Yes" && (
                  <Button
                    variant="outlined"
                    color="default"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleAddNewSite}
                    className={classes.addSiteButton}
                  >
                    Add New Site
                  </Button>
                )}
              </Grid>
            </Grid>
          </form>
          <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: "#707070", height: 1 }} />
        </div>
      </div>
    );
  }
);

export default SiteIDPlantIDDetails;
